import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useCurrentUser } from 'hooks/api/use-current-user';
import toast from 'components/partials/toast/toast';

import {
  saveNonModeledAdjustments,
  previewNonModeledAdjustment,
} from 'api/generated/aid-simulation-non-modeled-adjustments/aid-simulation-non-modeled-adjustments';
import type {
  AidModelResultRowDto,
  AidModelResultsResponseDto,
  NonAidRepackageDto,
  NonModeledAdjustmentActionDto,
  NonModeledAdjustmentDto,
} from 'api/generated/schemas';
import { NonModeledAdjustmentActionDtoAction } from 'api/generated/schemas/nonModeledAdjustmentActionDtoAction';

import { getGetAidModelResultsQueryKey } from '../../../hooks/use-aid-results';

import { formatCurrency, formatRate } from '../helpers/results-format';
import type { NonModeledRow } from '../results-step.types';

type DirtyFieldName =
  | 'adjustEnrollment'
  | 'adjustTuitionDiscountRate'
  | 'repackageWithNewRegimes';

interface DirtyCell {
  adjustEnrollment: number;
  adjustTuitionDiscountRate: number;
  repackageWithNewRegimes: boolean;
}

type DirtyState = Record<string, Partial<DirtyCell>>;

export interface DisplayNonModeledRow extends NonModeledRow {
  baselineEnrollmentValue: number;
  baselineTuitionDiscountRateValue: number;
  baselineRepackageWithNewRegimes: boolean;
  isDirty: boolean;
  isPreviewing: boolean;
}

export interface UseNonModeledAdjustmentsResult {
  rows: DisplayNonModeledRow[];
  allRepackaged: boolean;
  someRepackaged: boolean;
  totalUsingPreviousAwards: number;
  isDirtyGlobal: boolean;
  isSaving: boolean;
  handleEnrollmentChange: (row: NonModeledRow, value: number) => void;
  handleDiscountRateChange: (row: NonModeledRow, value: number) => void;
  handleRepackageToggle: (row: NonModeledRow, value: boolean) => void;
  handleRepackageToggleAll: (value: boolean) => void;
  handleRowUndo: (row: NonModeledRow) => void;
  handleRevertAll: () => void;
  handleSaveDraft: () => void;
}

const findPreviewRow = (
  response: AidModelResultsResponseDto | undefined,
  resultKey: string
): AidModelResultRowDto | undefined => {
  const est = response?.estimatedResults;

  if (est?.nonAid?.key === resultKey) return est.nonAid;

  return est?.excludedGroups?.find((group) => group.key === resultKey);
};

const findPreviewStatus = (
  response: AidModelResultsResponseDto | undefined,
  resultKey: string
): NonAidRepackageDto | undefined => {
  const est = response?.estimatedResults;

  if (est?.nonAid?.key === resultKey) return est.nonAidRepackage;

  return est?.excludedGroupRepackageStatuses?.[resultKey];
};

const applyDirtyField = (
  prev: DirtyState,
  resultKey: string,
  field: DirtyFieldName,
  value: number | boolean,
  baseline: number | boolean
): DirtyState => {
  const existing = prev[resultKey] ?? {};

  if (value === baseline) {
    const { [field]: _removed, ...rest } = existing;

    if (!Object.keys(rest).length) {
      const { [resultKey]: _removedRow, ...restState } = prev;

      return restState;
    }

    return { ...prev, [resultKey]: rest };
  }

  return { ...prev, [resultKey]: { ...existing, [field]: value } };
};

export const useNonModeledAdjustments = (
  baseRows: NonModeledRow[]
): UseNonModeledAdjustmentsResult => {
  const { modelId: modelIdParam } = useParams();
  const modelId = modelIdParam ? Number(modelIdParam) : 0;
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';
  const queryClient = useQueryClient();

  const [dirtyState, setDirtyState] = useState<DirtyState>({});
  const [previewResults, setPreviewResults] = useState<
    Record<string, AidModelResultsResponseDto>
  >({});
  const [previewingKeys, setPreviewingKeys] = useState<Set<string>>(new Set());
  const [isPersistingSave, setIsPersistingSave] = useState(false);
  const previewGenerationRef = useRef<Record<string, number>>({});

  const rows: DisplayNonModeledRow[] = useMemo(
    () =>
      baseRows.map((row) => {
        const dirty = dirtyState[row.resultKey];
        const enrollmentValue = dirty?.adjustEnrollment ?? row.enrollmentValue;
        const tuitionDiscountRateValue =
          dirty?.adjustTuitionDiscountRate ?? row.tuitionDiscountRateValue;
        const repackageWithNewRegimes =
          dirty?.repackageWithNewRegimes ?? row.repackageWithNewRegimes;

        const previewResponse = row.resultKey
          ? previewResults[row.resultKey]
          : undefined;
        const previewRow = previewResponse
          ? findPreviewRow(previewResponse, row.resultKey)
          : undefined;
        const previewStatus = previewResponse
          ? findPreviewStatus(previewResponse, row.resultKey)
          : undefined;

        return {
          ...row,
          baselineEnrollmentValue: row.enrollmentValue,
          baselineTuitionDiscountRateValue: row.tuitionDiscountRateValue,
          baselineRepackageWithNewRegimes: row.repackageWithNewRegimes,
          enrollmentValue,
          tuitionDiscountRateValue,
          originalEnrollmentValue: previewRow
            ? previewRow.originalEnrollment
            : row.originalEnrollmentValue,
          originalTuitionDiscountRateValue: previewRow
            ? previewRow.originalTuitionDiscountRate
            : row.originalTuitionDiscountRateValue,
          repackageWithNewRegimes,
          netTuitionRevenue: previewRow
            ? formatCurrency(previewRow.netTuitionRevenue)
            : row.netTuitionRevenue,
          institutionalAid: previewRow
            ? formatCurrency(previewRow.institutionalAid)
            : row.institutionalAid,
          tuitionDiscountRate: previewRow
            ? formatRate(previewRow.tuitionDiscountRate)
            : row.tuitionDiscountRate,
          repackageStatus: previewStatus ?? row.repackageStatus,
          isDirty: !!dirty,
          isPreviewing: !!row.resultKey && previewingKeys.has(row.resultKey),
        };
      }),
    [baseRows, dirtyState, previewResults, previewingKeys]
  );

  const adjustableRows = useMemo(
    () => rows.filter((row) => !!row.resultKey),
    [rows]
  );
  const allRepackaged =
    !!adjustableRows.length &&
    adjustableRows.every((row) => row.repackageWithNewRegimes);
  const someRepackaged = adjustableRows.some(
    (row) => row.repackageWithNewRegimes
  );
  const totalUsingPreviousAwards = rows.reduce(
    (sum, row) =>
      sum +
      (row.repackageWithNewRegimes
        ? row.repackageStatus?.notIncludedStudents ?? 0
        : 0),
    0
  );

  const isDirtyGlobal = !!Object.keys(dirtyState).length;

  const clearRowPreview = useCallback((resultKey: string) => {
    setPreviewResults((prev) => {
      if (!(resultKey in prev)) return prev;

      const { [resultKey]: _removed, ...rest } = prev;

      return rest;
    });
    setPreviewingKeys((prev) => {
      if (!prev.has(resultKey)) return prev;

      const next = new Set(prev);

      next.delete(resultKey);

      return next;
    });
  }, []);

  const clearAllPreviews = useCallback(() => {
    setPreviewResults({});
    setPreviewingKeys(new Set());
  }, []);

  const requestRowPreview = useCallback(
    (
      resultKey: string,
      effective: {
        adjustEnrollment: number;
        adjustTuitionDiscountRate: number;
        repackageWithNewRegimes: boolean;
      }
    ) => {
      const generation = (previewGenerationRef.current[resultKey] ?? 0) + 1;

      previewGenerationRef.current[resultKey] = generation;

      setPreviewingKeys((prev) => new Set(prev).add(resultKey));

      previewNonModeledAdjustment(userId, modelId, {
        key: resultKey,
        ...effective,
      })
        .then((response) => {
          if (previewGenerationRef.current[resultKey] !== generation) return;

          setPreviewResults((prev) => ({ ...prev, [resultKey]: response }));
        })
        .catch(() => {
          if (previewGenerationRef.current[resultKey] !== generation) return;

          setPreviewResults((prev) => {
            if (!(resultKey in prev)) return prev;

            const { [resultKey]: _removed, ...rest } = prev;

            return rest;
          });
          toast.error({
            title: 'Preview failed',
            message: 'Could not recalculate this row. Try editing again.',
          });
        })
        .finally(() => {
          if (previewGenerationRef.current[resultKey] !== generation) return;

          setPreviewingKeys((prev) => {
            if (!prev.has(resultKey)) return prev;

            const next = new Set(prev);

            next.delete(resultKey);

            return next;
          });
        });
    },
    [userId, modelId]
  );

  const triggerPreviewForRow = useCallback(
    (resultKey: string, overrides: Partial<DirtyCell>) => {
      const baseRow = baseRows.find((r) => r.resultKey === resultKey);

      if (!resultKey || !baseRow || !userId || !modelId) return;

      const pending: Partial<DirtyCell> = {
        ...dirtyState[resultKey],
        ...overrides,
      };
      const effectiveEnrollment =
        pending.adjustEnrollment ?? baseRow.enrollmentValue;
      const effectiveRate =
        pending.adjustTuitionDiscountRate ?? baseRow.tuitionDiscountRateValue;
      const effectiveRepackage =
        pending.repackageWithNewRegimes ?? baseRow.repackageWithNewRegimes;

      const isRowClean =
        effectiveEnrollment === baseRow.enrollmentValue &&
        effectiveRate === baseRow.tuitionDiscountRateValue &&
        effectiveRepackage === baseRow.repackageWithNewRegimes;

      if (isRowClean) {
        clearRowPreview(resultKey);

        return;
      }

      requestRowPreview(resultKey, {
        adjustEnrollment: effectiveEnrollment,
        adjustTuitionDiscountRate: effectiveRate,
        repackageWithNewRegimes: effectiveRepackage,
      });
    },
    [baseRows, dirtyState, clearRowPreview, requestRowPreview, userId, modelId]
  );

  const handleEnrollmentChange = useCallback(
    (row: NonModeledRow, value: number) => {
      const baseRow = baseRows.find((r) => r.resultKey === row.resultKey);

      setDirtyState((prev) =>
        applyDirtyField(
          prev,
          row.resultKey,
          'adjustEnrollment',
          value,
          baseRow?.enrollmentValue ?? 0
        )
      );
      triggerPreviewForRow(row.resultKey, { adjustEnrollment: value });
    },
    [baseRows, triggerPreviewForRow]
  );

  const handleDiscountRateChange = useCallback(
    (row: NonModeledRow, value: number) => {
      const baseRow = baseRows.find((r) => r.resultKey === row.resultKey);

      setDirtyState((prev) =>
        applyDirtyField(
          prev,
          row.resultKey,
          'adjustTuitionDiscountRate',
          value,
          baseRow?.tuitionDiscountRateValue ?? 0
        )
      );
      triggerPreviewForRow(row.resultKey, { adjustTuitionDiscountRate: value });
    },
    [baseRows, triggerPreviewForRow]
  );

  const handleRepackageToggle = useCallback(
    (row: NonModeledRow, value: boolean) => {
      const baseRow = baseRows.find((r) => r.resultKey === row.resultKey);

      setDirtyState((prev) =>
        applyDirtyField(
          prev,
          row.resultKey,
          'repackageWithNewRegimes',
          value,
          baseRow?.repackageWithNewRegimes ?? false
        )
      );
      triggerPreviewForRow(row.resultKey, { repackageWithNewRegimes: value });
    },
    [baseRows, triggerPreviewForRow]
  );

  const handleRepackageToggleAll = useCallback(
    (value: boolean) => {
      setDirtyState((prev) => {
        let next = prev;

        baseRows.forEach((row) => {
          if (!row.resultKey) return;

          next = applyDirtyField(
            next,
            row.resultKey,
            'repackageWithNewRegimes',
            value,
            row.repackageWithNewRegimes
          );
        });

        return next;
      });

      baseRows.forEach((row) => {
        if (!row.resultKey) return;

        triggerPreviewForRow(row.resultKey, { repackageWithNewRegimes: value });
      });
    },
    [baseRows, triggerPreviewForRow]
  );

  const handleRowUndo = useCallback(
    (row: NonModeledRow) => {
      setDirtyState((prev) => {
        const { [row.resultKey]: _removed, ...rest } = prev;

        return rest;
      });
      clearRowPreview(row.resultKey);
    },
    [clearRowPreview]
  );

  const handleRevertAll = useCallback(() => {
    setDirtyState({});
    clearAllPreviews();
  }, [clearAllPreviews]);

  const saveMutation = useMutation<
    NonModeledAdjustmentDto[],
    unknown,
    {
      oktaUserId: string;
      modelId: number;
      data: NonModeledAdjustmentActionDto[];
    }
  >({
    mutationFn: ({ oktaUserId, modelId: mId, data }) =>
      saveNonModeledAdjustments(oktaUserId, mId, data),
    onMutate: () => {
      setIsPersistingSave(true);
    },
    onSuccess: async () => {
      try {
        await queryClient.invalidateQueries({
          queryKey: getGetAidModelResultsQueryKey(userId, modelId),
        });
        setDirtyState({});
        clearAllPreviews();
        toast.success({
          title: 'Adjustments were saved',
          message: 'Updated values are baseline now.',
        });
      } finally {
        setIsPersistingSave(false);
      }
    },
    onError: () => {
      setIsPersistingSave(false);
      toast.error({
        title: 'Could not save adjustments',
        message: 'Your changes were not saved. Try again.',
      });
    },
  });

  const handleSaveDraft = useCallback(() => {
    const actions: NonModeledAdjustmentActionDto[] = Object.entries(
      dirtyState
    ).map(([resultKey, dirty]) => {
      const row = baseRows.find((r) => r.resultKey === resultKey);

      return {
        action: NonModeledAdjustmentActionDtoAction.SAVE,
        key: resultKey,
        adjustEnrollment: dirty.adjustEnrollment,
        adjustTuitionDiscountRate: dirty.adjustTuitionDiscountRate,
        repackageWithNewRegimes:
          dirty.repackageWithNewRegimes ?? row?.repackageWithNewRegimes,
      };
    });

    if (!actions.length) return;

    saveMutation.mutate({ oktaUserId: userId, modelId, data: actions });
  }, [dirtyState, baseRows, saveMutation, userId, modelId]);

  return {
    rows,
    allRepackaged,
    someRepackaged,
    totalUsingPreviousAwards,
    isDirtyGlobal,
    isSaving: saveMutation.isPending || isPersistingSave,
    handleEnrollmentChange,
    handleDiscountRateChange,
    handleRepackageToggle,
    handleRepackageToggleAll,
    handleRowUndo,
    handleRevertAll,
    handleSaveDraft,
  };
};
